'use client';

import { c as _c } from "react/compiler-runtime";
import { cn } from 'cn';
import { AlertTriangle, RotateCw } from 'lucide-react';
import { useClerkRefreshIndicator } from '../hooks/use-clerk-refresh-indicator';

/**
 * Tiny non-blocking status pill anchored to the top-center of the viewport.
 * Driven by {@link useClerkRefreshIndicator} (which is itself driven by the
 * pub/sub state in `clerk-session-bridge`).
 *
 * - `refreshing` → animated pulse + "Reconnecting…". Only shown after the
 *   debounce in `CLERK_SESSION_REFRESH_TIMING.indicator`, so fast refreshes
 *   never flash the chrome.
 * - `failed`     → warning icon + "Reconnection failed". Auto-clears per
 *   the same timing config.
 *
 * Mounted once near `<ClerkSessionBridge />` so it has the same lifetime
 * as the bridge. Renders `null` outside the two visible states.
 */
export function ClerkRefreshIndicator() {
  const $ = _c(10);
  const view = useClerkRefreshIndicator();
  if (view.kind === "idle") {
    return null;
  }
  const isFailed = view.kind === "failed";
  const t0 = isFailed ? "border-destructive/30 bg-card text-destructive" : "border-border bg-card text-muted-foreground";
  let t1;
  if ($[0] !== t0) {
    t1 = cn("pointer-events-none fixed top-2 left-1/2 z-50 flex -translate-x-1/2 items-center gap-2 rounded-full border px-3 py-1.5 text-xs shadow-sm backdrop-blur", "transition-opacity duration-200", t0);
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== isFailed) {
    t2 = isFailed ? <AlertTriangle className="h-3.5 w-3.5" /> : <RotateCw className="h-3.5 w-3.5 animate-spin" />;
    $[2] = isFailed;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = isFailed ? "Reconnection failed" : "Reconnecting\u2026";
  let t4;
  if ($[4] !== t3) {
    t4 = <span>{t3}</span>;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t1 || $[7] !== t2 || $[8] !== t4) {
    t5 = <div role="status" aria-live="polite" className={t1} data-sentry-component="ClerkRefreshIndicator" data-sentry-source-file="clerk-refresh-indicator.tsx">{t2}{t4}</div>;
    $[6] = t1;
    $[7] = t2;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
}